// src/components/modals/SettingsModal.js
// ✅ WITH MONTHLY/ANNUAL BILLING TOGGLE
// Vault/research-lab reskin — presentation only, all handlers/state/props preserved.
import React, { useState } from 'react';
import {
  X, User, CreditCard, Bell, Palette, Shield, HelpCircle,
  Mail, Lock, Trash2, Download, Moon, Sun, Globe, ExternalLink, Check,
  Lightbulb
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import * as apiService from '../../services/apiService';
import './SettingsModal.css';

const SettingsModal = ({ isOpen, onClose, theme: propTheme, setTheme: propSetTheme }) => {
  const { user, userData, updateUserData, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // monthly or annual
  const [loading, setLoading] = useState(false);
  const theme = propTheme || localStorage.getItem('theme') || 'dark';
  const setTheme = propSetTheme || (() => {});
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    updates: true
  });

  if (!isOpen) return null;

  const tabs = [
    { id: 'account', icon: User, label: 'Account' },
    { id: 'subscription', icon: CreditCard, label: 'Subscription' },
    { id: 'credits', icon: CreditCard, label: 'Buy Credits' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'appearance', icon: Palette, label: 'Appearance' },
    { id: 'privacy', icon: Shield, label: 'Privacy' },
    { id: 'help', icon: HelpCircle, label: 'Help' }
  ];

  // Stripe integration for subscriptions
  const handleSubscribe = async (priceId) => {
    try {
      setLoading(true);
      const userHash = localStorage.getItem('qxai_user_hash');
      if (!userHash) {
        alert('Please sign in to subscribe');
        return;
      }

      const response = await apiService.createCheckoutSession(userHash, priceId, 'subscription');

      if (response.sessionUrl) {
        window.location.href = response.sessionUrl;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Stripe integration for credit purchases
  const handleBuyCredits = async (priceId, credits) => {
    try {
      setLoading(true);
      const userHash = localStorage.getItem('qxai_user_hash');
      if (!userHash) {
        alert('Please sign in to purchase credits');
        return;
      }

      const response = await apiService.purchaseCredits(userHash, priceId, credits);

      if (response.sessionUrl) {
        window.location.href = response.sessionUrl;
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
      alert('Failed to purchase credits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (newTheme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      console.log('Delete account');
    }
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'qxai-data.json';
    link.click();
  };

  // ========== ACCOUNT TAB ==========
  const renderAccountTab = () => (
    <div className="vs-stack">
      <h3 className="vs-section-title">Account Information</h3>

      <div className="vs-group">
        <label className="vault-label"><Mail size={14} /><span>Email</span></label>
        <input
          type="email"
          value={user?.email || ''}
          disabled
          className="vault-field"
          style={{ opacity: 0.6, cursor: 'not-allowed' }}
        />
      </div>

      <div className="vs-group">
        <label className="vault-label"><User size={14} /><span>Display Name</span></label>
        <input
          type="text"
          placeholder="Your name"
          className="vault-field"
        />
      </div>

      <div className="vs-group">
        <label className="vault-label"><Lock size={14} /><span>Password</span></label>
        <button className="vault-btn">Change Password</button>
      </div>

      <div className="vs-danger-zone">
        <h4 className="vs-danger-title">Danger Zone</h4>
        <div className="vs-row-wrap">
          <button className="vault-btn vault-btn--danger" onClick={handleDeleteAccount}>
            <Trash2 size={15} />
            Delete Account
          </button>
          <button className="vault-btn" onClick={signOut}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  // ========== SUBSCRIPTION TAB WITH BILLING TOGGLE ==========
  const renderSubscriptionTab = () => {
    // Default to Starter plan for accounts without tier
    const currentTier = userData?.tier?.toLowerCase() || 'starter';
    // Test account gets 1000 credits, others get their actual credits or 160 (Starter default)
    const isTestAccount = user?.email === 'test@qxai.space';
    const defaultCredits = isTestAccount ? 1000 : 160;
    const displayCredits = userData?.credits ?? defaultCredits;
    const hasPaidPlan = currentTier !== 'none' && currentTier !== 'free';

    const plans = [
      {
        id: 'starter',
        name: 'Starter',
        monthly: {
          price: 18.95,
          priceId: 'price_1SR09vKrDWVDJRBkzpYB4MAh',
          credits: 160
        },
        annual: {
          price: 215.10,
          priceId: 'price_1SR0BsKrDWVDJRBkbM8UMg9Q',
          credits: 1920,
          monthlyEquivalent: 17.93
        },
        features: [
          '160 credits/month (1,920/year)',
          'All AI models',
          'Image & video generation',
          'Document export',
          'Email support'
        ]
      },
      {
        id: 'pro',
        name: 'Pro',
        monthly: {
          price: 24.95,
          priceId: 'price_1SR0CgKrDWVDJRBkCP1p2JZ7',
          credits: 320
        },
        annual: {
          price: 284.10,
          priceId: 'price_1SR0DqKrDWVDJRBkfVvQBPeM',
          credits: 3840,
          monthlyEquivalent: 23.68
        },
        popular: true,
        features: [
          '320 credits/month (3,840/year)',
          'All AI models',
          'Priority processing',
          'Advanced features',
          'Priority support',
          'No rate limits'
        ]
      },
      {
        id: 'superpro',
        name: 'Super Pro',
        monthly: {
          price: 49.95,
          priceId: 'price_1SR0EsKrDWVDJRBkRpGJv94z',
          credits: 400
        },
        annual: {
          price: 569.43,
          priceId: 'price_1SR0FkKrDWVDJRBkLggs2Sut',
          credits: 4800,
          monthlyEquivalent: 47.45
        },
        features: [
          '400 credits/month (4,800/year)',
          'All models + beta access',
          'Fastest processing',
          'Custom workflows',
          'Dedicated support',
          'API access',
          'Team features'
        ]
      }
    ];

    return (
      <div className="vs-stack">
        <div className="vs-head-row">
          <h3 className="vs-section-title">Subscription Plans</h3>

          {/* Billing Period Toggle */}
          <div className="vault-tabs vs-billing-toggle">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`vault-tab ${billingPeriod === 'monthly' ? 'is-active' : ''}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`vault-tab ${billingPeriod === 'annual' ? 'is-active' : ''}`}
            >
              Annual
              <span className="vault-badge vault-badge--free vs-save-badge">Save 5%</span>
            </button>
          </div>
        </div>

        {/* Current Plan Card */}
        {hasPaidPlan ? (
          <div className="vs-current-plan">
            <div className="vs-current-plan-head">
              <h4 className="vs-current-plan-name">{currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan</h4>
              <span className="vault-badge vault-badge--free">Active</span>
            </div>
            <div className="vs-current-plan-body">
              <p><strong>Credits:</strong> {displayCredits}</p>
              <p><strong>Billing:</strong> {billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</p>
              <p><strong>Next Billing:</strong> {new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}</p>
              <p className="vs-fine-print">Credits reset monthly and don't roll over.</p>
            </div>
          </div>
        ) : (
          <div className="vs-current-plan vs-current-plan--none">
            <h4 className="vs-current-plan-name">No Active Subscription</h4>
            <p className="vs-muted">Choose a plan below to get started with QXAI premium features.</p>
          </div>
        )}

        {/* Available Plans */}
        <div className="vs-plan-grid">
          {plans.map((plan) => {
            const pricing = billingPeriod === 'annual' ? plan.annual : plan.monthly;
            const isCurrentPlan = currentTier === plan.id;

            return (
              <div
                key={plan.id}
                className={`vs-tier-card ${plan.popular ? 'is-selected' : ''}`}
              >
                {plan.popular && (
                  <div className="vs-tier-flag">
                    <span className="vault-badge vault-badge--premium">Most Popular</span>
                  </div>
                )}

                <h5 className="vs-tier-name">{plan.name}</h5>

                {billingPeriod === 'annual' ? (
                  <>
                    <p className="vs-tier-price">
                      ${pricing.price}
                      <span className="vs-tier-per">/year</span>
                    </p>
                    <p className="vs-tier-subprice">
                      ${pricing.monthlyEquivalent.toFixed(2)}/mo • Save ${(plan.monthly.price * 12 - pricing.price).toFixed(2)}/year
                    </p>
                  </>
                ) : (
                  <p className="vs-tier-price vs-tier-price--mb">
                    ${pricing.price}
                    <span className="vs-tier-per">/mo</span>
                  </p>
                )}

                <ul className="vs-feature-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="vs-feature">
                      <Check size={16} className="vs-feature-check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <button disabled className="vault-btn vs-full">
                    Current Plan
                  </button>
                ) : (
                  <button
                    className="vault-btn vault-btn--primary vs-full"
                    onClick={() => handleSubscribe(pricing.priceId)}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Price Disclaimer */}
        <p className="vs-disclaimer">*Prices subject to change</p>

        {/* Manage Subscription */}
        {hasPaidPlan && (
          <div className="vs-divider-block">
            <h4 className="vs-subhead">Manage Subscription</h4>
            <p className="vs-muted vs-mb">
              Need to cancel or modify your subscription? Contact our support team. Cancellation takes effect at the end of your current billing period.
            </p>
            <a
              href="mailto:support@qxai.space?subject=Subscription Management Request"
              className="vault-btn vault-btn--primary"
            >
              <Mail size={15} />
              Contact Support
            </a>
          </div>
        )}
      </div>
    );
  };

  // ========== CREDIT PACKAGES TAB ==========
  const renderCreditsTab = () => {
    const packages = [
      {
        id: 'small',
        name: 'Small Pack',
        credits: 100,
        price: 15.00,
        priceId: 'price_1SLU27KrDWVDJRBk6x8OyIy3'
      },
      {
        id: 'medium',
        name: 'Medium Pack',
        credits: 250,
        price: 30.00,
        priceId: 'price_1SLU5EKrDWVDJRBkVL8QcIrS',
        popular: true
      },
      {
        id: 'large',
        name: 'Large Pack',
        credits: 500,
        price: 50.00,
        priceId: 'price_1SLUCQKrDWVDJRBkZD39RcbP'
      }
    ];

    return (
      <div className="vs-stack">
        <div>
          <h3 className="vs-section-title">Buy Credits</h3>
          <p className="vs-muted">
            Purchase additional credits to use premium AI models and features
          </p>
        </div>

        {/* Current Balance */}
        <div className="vs-balance-card">
          <div className="vs-balance-row">
            <div>
              <p className="vault-label vs-balance-label">Current Balance</p>
              <p className="vs-balance-num">{userData?.credits || 0}</p>
              <p className="vs-muted">credits</p>
            </div>
            <CreditCard size={44} className="vs-balance-icon" />
          </div>
        </div>

        {/* Credit Packages */}
        <div className="vs-pack-grid">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`vs-tier-card ${pkg.popular ? 'is-selected' : ''}`}
            >
              {pkg.popular && (
                <div className="vs-tier-flag">
                  <span className="vault-badge vault-badge--premium">Best Value</span>
                </div>
              )}

              <div className="vs-pack-head">
                <div>
                  <h5 className="vs-tier-name">{pkg.name}</h5>
                  <p className="vs-muted vs-pack-credits">{pkg.credits.toLocaleString()} credits</p>
                </div>
                <p className="vs-tier-price vs-tier-price--sm">${pkg.price}</p>
              </div>

              <div className="vs-pack-rate">
                ${(pkg.price / pkg.credits * 100).toFixed(2)} per 100 credits
              </div>

              <button
                className="vault-btn vault-btn--primary vs-full"
                onClick={() => handleBuyCredits(pkg.priceId, pkg.credits)}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Buy Now'}
              </button>
            </div>
          ))}
        </div>

        {/* Credit Info */}
        <div className="vs-info-card">
          <h4 className="vs-info-title">
            <Lightbulb size={16} strokeWidth={1.5} /> About Credits
          </h4>
          <ul className="vs-info-list">
            <li>• Credits never expire</li>
            <li>• Use across all AI models and features</li>
            <li>• Free models cost 0 credits</li>
            <li>• Premium models: 10-50 credits per generation</li>
          </ul>
        </div>
      </div>
    );
  };

  // ========== NOTIFICATIONS TAB ==========
  const renderNotificationsTab = () => (
    <div className="vs-stack">
      <h3 className="vs-section-title">Notification Preferences</h3>

      <div className="vs-toggle-row">
        <div>
          <h4 className="vs-toggle-title">Email Notifications</h4>
          <p className="vs-muted">Receive updates and announcements</p>
        </div>
        <label className="vs-switch">
          <input
            type="checkbox"
            checked={notifications.email}
            onChange={(e) => setNotifications({...notifications, email: e.target.checked})}
          />
          <span className="vs-switch-track"></span>
        </label>
      </div>

      <div className="vs-toggle-row">
        <div>
          <h4 className="vs-toggle-title">Push Notifications</h4>
          <p className="vs-muted">Get notified about completions</p>
        </div>
        <label className="vs-switch">
          <input
            type="checkbox"
            checked={notifications.push}
            onChange={(e) => setNotifications({...notifications, push: e.target.checked})}
          />
          <span className="vs-switch-track"></span>
        </label>
      </div>

      <div className="vs-toggle-row">
        <div>
          <h4 className="vs-toggle-title">Product Updates</h4>
          <p className="vs-muted">Learn about new features</p>
        </div>
        <label className="vs-switch">
          <input
            type="checkbox"
            checked={notifications.updates}
            onChange={(e) => setNotifications({...notifications, updates: e.target.checked})}
          />
          <span className="vs-switch-track"></span>
        </label>
      </div>
    </div>
  );

  // ========== APPEARANCE TAB ==========
  const renderAppearanceTab = () => (
    <div className="vs-stack">
      <h3 className="vs-section-title">Appearance</h3>

      <div className="vs-group">
        <label className="vault-label">Theme</label>
        <div className="vs-theme-grid">
          <button
            className={`vs-theme-card ${theme === 'light' ? 'is-selected' : ''}`}
            onClick={() => handleThemeChange('light')}
          >
            <Sun size={24} className="vs-theme-icon" />
            <span className="vs-theme-name">Light</span>
          </button>

          <button
            className={`vs-theme-card ${theme === 'dark' ? 'is-selected' : ''}`}
            onClick={() => handleThemeChange('dark')}
          >
            <Moon size={24} className="vs-theme-icon" />
            <span className="vs-theme-name">Dark</span>
          </button>

          <button
            className={`vs-theme-card ${theme === 'auto' ? 'is-selected' : ''}`}
            onClick={() => handleThemeChange('auto')}
          >
            <Globe size={24} className="vs-theme-icon" />
            <span className="vs-theme-name">Auto</span>
          </button>
        </div>
      </div>
    </div>
  );

  // ========== PRIVACY TAB ==========
  const renderPrivacyTab = () => (
    <div className="vs-stack">
      <div>
        <h3 className="vs-section-title">Privacy &amp; Security</h3>
        <p className="vs-muted vs-inline-icon">
          <Lock size={14} strokeWidth={1.5} /> AI Without Surveillance — Your data stays private
        </p>
      </div>

      <div className="vs-info-card">
        <h4 className="vs-info-title">
          <Shield size={18} strokeWidth={1.5} /> Privacy Features
        </h4>
        <ul className="vs-info-list vs-info-list--checks">
          <li><Check size={14} strokeWidth={2} className="vs-feature-check" /><span>Hash-based authentication (no email storage on backend)</span></li>
          <li><Check size={14} strokeWidth={2} className="vs-feature-check" /><span>Conversations stored locally in your browser</span></li>
          <li><Check size={14} strokeWidth={2} className="vs-feature-check" /><span>No conversation data sent to third parties</span></li>
          <li><Check size={14} strokeWidth={2} className="vs-feature-check" /><span>Optional cloud sync (encrypted)</span></li>
          <li><Check size={14} strokeWidth={2} className="vs-feature-check" /><span>Complete data export anytime</span></li>
        </ul>
      </div>

      <div className="vs-toggle-row">
        <div>
          <h4 className="vs-toggle-title">Save Conversation History</h4>
          <p className="vs-muted">Store locally in browser</p>
        </div>
        <label className="vs-switch">
          <input type="checkbox" defaultChecked />
          <span className="vs-switch-track"></span>
        </label>
      </div>

      <div>
        <button className="vault-btn vault-btn--primary" onClick={handleExportData}>
          <Download size={15} />
          Export My Data
        </button>
      </div>
    </div>
  );

  // ========== HELP TAB ==========
  const renderHelpTab = () => (
    <div className="vs-stack">
      <h3 className="vs-section-title">Help &amp; Support</h3>

      <div className="vs-group">
        <a
          href="https://docs.qxai.space"
          target="_blank"
          rel="noopener noreferrer"
          className="vs-link-card"
        >
          <HelpCircle size={22} className="vs-link-icon" />
          <div className="vs-link-text">
            <h4 className="vs-toggle-title">Documentation</h4>
            <p className="vs-muted">Learn how to use QXAI</p>
          </div>
          <ExternalLink size={16} className="vs-link-ext" />
        </a>

        <a
          href="mailto:support@qxai.space"
          className="vs-link-card"
        >
          <Mail size={22} className="vs-link-icon" />
          <div className="vs-link-text">
            <h4 className="vs-toggle-title">Contact Support</h4>
            <p className="vs-muted">support@qxai.space</p>
          </div>
        </a>

        <a
          href="https://discord.gg/qxai"
          target="_blank"
          rel="noopener noreferrer"
          className="vs-link-card"
        >
          <Globe size={22} className="vs-link-icon" />
          <div className="vs-link-text">
            <h4 className="vs-toggle-title">Community</h4>
            <p className="vs-muted">Join our Discord</p>
          </div>
          <ExternalLink size={16} className="vs-link-ext" />
        </a>
      </div>

      <div className="vs-divider-block">
        <h4 className="vs-subhead">About</h4>
        <p className="vs-muted">QXAI - AI Without Surveillance</p>
        <p className="vs-muted">Version 1.0.0</p>
        <p className="vs-muted">© 2025 QXAI. All rights reserved.</p>
      </div>
    </div>
  );

  // ========== MAIN RENDER ==========
  return (
    <div className="vault-overlay" onClick={onClose}>
      <div
        className="vault-modal vault-modal--wide vs-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="vault-modal-head">
          <h2 className="vault-modal-title">Settings</h2>
          <div className="vault-modal-sub">QXAI · Control Panel</div>
          <span className="spacer" />
          <button className="vault-icon-btn" onClick={onClose} aria-label="Close settings">
            <X size={18} />
          </button>
        </div>

        <div className="vs-body">
          <nav className="vs-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`vs-nav-item ${activeTab === tab.id ? 'is-active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="vs-content">
            {activeTab === 'account' && renderAccountTab()}
            {activeTab === 'subscription' && renderSubscriptionTab()}
            {activeTab === 'credits' && renderCreditsTab()}
            {activeTab === 'notifications' && renderNotificationsTab()}
            {activeTab === 'appearance' && renderAppearanceTab()}
            {activeTab === 'privacy' && renderPrivacyTab()}
            {activeTab === 'help' && renderHelpTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
